import { mkHeaders, UnauthorisedError, ApiError } from "./utils"

export interface Endorsement {
  name: string
  children: Endorsement[] | null
  isZip?: boolean
}

export const listEndorsements = async (
  baseUrl: string,
  token: string,
): Promise<Endorsement> => {
  const res = await fetch(`${baseUrl}/api/endorsements`, {
    headers: mkHeaders(token),
    credentials: "include",
  })

  if (res.status === 401) {
    throw new UnauthorisedError(
      `Unauthorised access - operation=listEndorsements statusCode=${res.status}`,
    )
  }

  if (res.status !== 200) {
    const msg = await res.text()
    throw new ApiError(
      `Failed to list endorsements - statusCode=${res.status} message=${msg}`,
      res.status,
      "listEndorsements",
    )
  }

  const docs: Endorsement = await res.json()
  return docs
}

export const listEndorsementsByUMR: {
  (baseUrl: string, token: string, umr: string): Promise<Endorsement>
  (baseUrl: string, token: string): Promise<Endorsement>
} = async (
  baseUrl: string,
  token: string,
  umr?: string,
): Promise<Endorsement> => {
  const url = umr
    ? `${baseUrl}/api/endorsements/${encodeURIComponent(umr)}`
    : `${baseUrl}/api/endorsements`

  const res = await fetch(url, {
    headers: mkHeaders(token),
    credentials: "include",
  })

  if (res.status === 401) {
    throw new UnauthorisedError(
      `Unauthorised access - operation=listEndorsementsByUMR statusCode=${res.status} umr=${umr}`,
    )
  }

  if (res.status !== 200) {
    const msg = await res.text()
    throw new ApiError(
      `Failed to list endorsements - statusCode=${res.status} umr=${umr} message=${msg}`,
      res.status,
      "listEndorsementsByUMR",
      { umr },
    )
  }

  return await res.json()
}

export interface SignedUrl {
  url: string
  contentType: string
}

export const getEndorsementPresignedUrl = async (
  baseUrl: string,
  token: string,
  fileName: string,
): Promise<SignedUrl> => {
  const res = await fetch(
    `${baseUrl}/api/endorsements/url?fileName=${encodeURIComponent(fileName)}`,
    {
      headers: mkHeaders(token),
      credentials: "include",
    },
  )

  if (res.status === 401) {
    throw new UnauthorisedError(
      `Unauthorised access - operation=getEndorsementPresignedUrl statusCode=${res.status} fileName=${fileName}`,
    )
  }

  if (res.status !== 200) {
    const msg = await res.text()
    throw new ApiError(
      `Failed to get endorsement presigned URL - statusCode=${res.status} fileName=${fileName} message=${msg}`,
      res.status,
      "getEndorsementPresignedUrl",
      { fileName },
    )
  }

  const signedUrl: SignedUrl = await res.json()
  return signedUrl
}
